import { ref, getCurrentInstance } from 'vue';

function install(app, { ti18n, rerenderOn = ['languageChanged', 'loaded', 'added', 'removed'], }) {
    const genericT = ti18n.t.bind(ti18n);
    const lastI18nChange = ref(new Date());
    const invalidate = () => lastI18nChange.value = new Date();
    const usingI18n = () => lastI18nChange.value;
    rerenderOn.forEach((event) => {
        switch (event) {
            case 'added':
            case 'removed':
                ti18n === null || ti18n === void 0 ? void 0 : ti18n.on(event, invalidate);
                break;
            default:
                ti18n.on(event, invalidate);
                break;
        }
    });
    app.mixin({
        beforeCreate() {
            var _a, _b;
            const options = this.$options;
            if (!options.__i18n && !options.i18nOptions) {
                this.__translate = undefined;
                return;
            }
            const { name } = this.$options;
            const rand = ((Math.random() * 10 ** 8) | 0).toString();
            const localNs = [name, rand].filter(x => !!x).join('-');
            this.__bundles = [];
            const loadBundle = (bundle) => {
                Object.entries(bundle).forEach(([lng, resources]) => {
                    ti18n.addResourceBundle(lng, localNs, resources, true, false);
                    this.__bundles.push([lng, localNs]);
                });
            };
            (_a = options.__i18n) === null || _a === void 0 ? void 0 : _a.forEach((bundle) => {
                loadBundle(JSON.parse(bundle));
            });
            const { lng, keyPrefix } = handleI18nOptions(options, loadBundle);
            let { ns } = handleI18nOptions(options, loadBundle);
            if ((_b = this.__bundles) === null || _b === void 0 ? void 0 : _b.length) {
                ns = [localNs].concat(ns !== null && ns !== void 0 ? ns : []);
            }
            const t = getTranslationFunction(lng, ns);
            this.__translate = (key, options) => {
                if (!keyPrefix || includesNs(key)) {
                    return t(key, options);
                }
                return t(`${keyPrefix}.${key}`, options);
            };
        },
        unmounted() {
            var _a;
            (_a = this.__bundles) === null || _a === void 0 ? void 0 : _a.forEach(([lng, ns]) => ti18n.removeResourceBundle(lng, ns));
        },
    });
    app.config.globalProperties.$t = function (key, options) {
        var _a;
        usingI18n();
        if (ti18n.isInitialized) {
            return ((_a = this === null || this === void 0 ? void 0 : this.__translate) !== null && _a !== void 0 ? _a : genericT)(key, options);
        }
        return key;
    };
    app.config.globalProperties.$ti18n = new Proxy(ti18n, {
        get(target, prop) {
            usingI18n();
            return Reflect.get(target, prop);
        },
    });
    function getTranslationFunction(lng, ns) {
        if (lng) {
            return ti18n.getFixedT(lng, ns);
        }
        if (ns) {
            return ti18n.getFixedT(null, ns);
        }
        return genericT;
    }
    function includesNs(key) {
        const { nsSeparator } = ti18n.options;
        return typeof nsSeparator === 'string' && key.includes(nsSeparator);
    }
    function handleI18nOptions(options, loadBundle) {
        let lng;
        let ns;
        let keyPrefix;
        if (options.i18nOptions) {
            let messages;
            let namespaces;
            ({
                lng,
                namespaces = ti18n.options.defaultNS,
                keyPrefix,
                messages,
            } = options.i18nOptions);
            if (messages) {
                loadBundle(messages);
            }
            ns = typeof namespaces === 'string' ? [namespaces] : namespaces;
            if (ns) {
                ti18n.loadNamespaces(ns);
            }
        }
        return { lng, ns, keyPrefix };
    }
}
function useTranslation() {
    const instance = getCurrentInstance();
    if (!instance) {
        throw new Error('ti18n-vue: No Vue instance in context. Make sure to register the ti18n-vue plugin using app.use(...).');
    }
    const globalProps = instance.appContext.config.globalProperties;
    return {
        ti18n: globalProps.$ti18n,
        t: globalProps.$t,
    };
}

export { install as default, useTranslation };
